import React from "react";
import {
  FocusZone,
  FocusZoneDirection,
  getTheme,
  IColumn,
  IDetailsHeaderProps,
  mergeStyles,
} from "@fluentui/react";

export interface IMultiColumnHeaderColumn extends IColumn {
  multiColumnHeader?: string;
}

interface MultiHeaderGroup {
  key: string;
  name: string;
  columns: IMultiColumnHeaderColumn[];
}

function createMultiColumnHeaderGroups(
  columns: IMultiColumnHeaderColumn[],
): MultiHeaderGroup[] {
  const groups: MultiHeaderGroup[] = [];
  let currentGroup: MultiHeaderGroup | null = null;

  columns.forEach((column, index) => {
    const header = column.multiColumnHeader;
    if (header && (!currentGroup || currentGroup.name !== header)) {
      currentGroup = {
        key: `multi_header_${index}`,
        name: header,
        columns: [column],
      };
      groups.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.columns.push(column);
    }
  });

  return groups;
}

const theme = getTheme();
const borderStyle = `1px solid ${theme.palette.neutralLight}`;

// Fluent UI DetailsHeader adds this right padding to each column cell (except the last)
const CELL_RIGHT_PADDING = 20;

const wrapperStyle = mergeStyles({
  display: "inline-block",
});

const multiHeaderRowStyle = mergeStyles({
  display: "block",
  minWidth: "100%",
  whiteSpace: "nowrap",
});

const multiHeaderCellBaseStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "0.5rem 0.5rem 0.75rem 0.75rem",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  borderBottom: borderStyle,
  fontSize: "0.875rem",
  fontWeight: 600,
  boxSizing: "content-box",
  textAlign: "left",
  background: theme.palette.white,
  height: "auto",
  verticalAlign: "top",
};

export function renderMultiColumnDetailsHeader(
  props: IDetailsHeaderProps | undefined,
  defaultRender:
    | ((props?: IDetailsHeaderProps) => React.ReactElement | null)
    | undefined,
): React.ReactElement | null {
  if (!props || !defaultRender) return null;

  const columns = props.columns as IMultiColumnHeaderColumn[];
  const hasMultiHeader = columns.some((c) => !!c.multiColumnHeader);

  if (!hasMultiHeader) {
    return defaultRender(props);
  }

  const groups = createMultiColumnHeaderGroups(columns);

  return (
    <div className={wrapperStyle}>
      <FocusZone
        role="row"
        direction={FocusZoneDirection.horizontal}
        className={multiHeaderRowStyle}
      >
        {groups.map((group, idx) => {
          // Width = sum of calculatedWidths + inter-cell padding
          // This matches how DetailsHeader sizes cells: each cell gets
          // calculatedWidth + CELL_RIGHT_PADDING (except the last column)
          const width = group.columns.reduce(
            (w, col) => {
              return w + (col.calculatedWidth ?? col.minWidth);
            },
            (group.columns.length - 1) * CELL_RIGHT_PADDING,
          );

          return (
            <div
              key={group.key}
              role="columnheader"
              data-item-key={group.key}
              style={{
                ...multiHeaderCellBaseStyle,
                width,
                borderLeft: idx === 0 ? "none" : borderStyle,
              }}
            >
              {group.name}
            </div>
          );
        })}
      </FocusZone>
      {defaultRender(props)}
    </div>
  );
}
